import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import ArchiveRoundedIcon from '@mui/icons-material/ArchiveRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import RestoreRoundedIcon from '@mui/icons-material/RestoreRounded';
import RuleRoundedIcon from '@mui/icons-material/RuleRounded';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { useArchiveProduct, useProductPublication, useRestoreProduct } from '../../hooks';
import { formatDateTime } from '../../lib/format';
import { useCatalogPermissions } from '../../lib/useCatalogPermissions';
import type { ToastSeverity } from '../../lib/useToast';
import {
  COMMERCIAL_AVAILABILITY_LABELS,
  PRODUCT_CARD_STATUS_LABELS,
  LIFECYCLE_STATUS_LABELS,
  commercialAvailabilityColor,
  productCardStatusColor,
  PUBLICATION_STATUS_LABELS,
  publicationStatusColor,
} from '../../model/options';
import type { ChannelPublication } from '../../model/types';

interface ProductPublicationCardProps {
  productId: string;
  onResult: (severity: ToastSeverity, message: string) => void;
}

function channelLabel(channel: ChannelPublication) {
  return channel.channelName ? `${channel.channelName} (${channel.channelCode})` : channel.channelCode;
}

function channelTooltip(t: (key: string, vars?: Record<string, unknown>) => string, channel: ChannelPublication) {
  const parts: string[] = [];
  if (channel.publishedAt) {
    parts.push(t('productPublicationCard.channelPublishedTooltip', { date: formatDateTime(channel.publishedAt) }));
  }
  if (channel.reason) {
    parts.push(t('productPublicationCard.reasonTooltip', { reason: channel.reason }));
  }
  if (channel.missingRequiredFields?.length) {
    parts.push(t('productPublicationCard.missingFieldsTooltip', { fields: channel.missingRequiredFields.join(', ') }));
  }
  return parts.join('\n');
}

/**
 * Read-only product publication diagnostics. Product-to-channel membership is configured in the
 * publication workspace through sales-channel catalog bindings and rules; this card only surfaces the
 * resulting state next to the product and keeps master-data lifecycle actions (archive/restore).
 */
export function ProductPublicationCard({ productId, onResult }: ProductPublicationCardProps) {
  const { t } = useTranslation('catalog');
  const { canManage } = useCatalogPermissions();
  const publicationQuery = useProductPublication(productId);
  const archive = useArchiveProduct();
  const restore = useRestoreProduct();

  const [confirmArchive, setConfirmArchive] = useState(false);

  const publication = publicationQuery.data;
  const channels = publication?.channels ?? [];
  const publishedChannels = channels.filter((c) => c.publicationStatus === 'PUBLISHED' || c.published);
  const blockedChannels = channels.filter(
    (c) => c.publicationStatus === 'FAILED_PROFILE' || c.publicationStatus === 'NO_PRIMARY_CATALOG',
  );
  const awaitingChannels = channels.filter(
    (c) =>
      c.publicationStatus === 'INCLUDED' ||
      (c.included && !c.published && !blockedChannels.some((blocked) => blocked.channelCode === c.channelCode)),
  );
  const isArchived = publication?.lifecycleStatus === 'ARCHIVED' || publication?.cardStatus === 'ARCHIVED';
  const pending = archive.isPending || restore.isPending;

  const runArchive = () =>
    archive.mutate(
      { productId },
      {
        onSuccess: () => {
          setConfirmArchive(false);
          onResult('success', t('productPublicationCard.archived'));
        },
        onError: (err) => {
          setConfirmArchive(false);
          onResult('error', err.message);
        },
      },
    );

  const runRestore = () =>
    restore.mutate(
      { productId },
      {
        onSuccess: () => onResult('success', t('productPublicationCard.restored')),
        onError: (err) => onResult('error', err.message),
      },
    );

  const cardStatusKey = PRODUCT_CARD_STATUS_LABELS[publication?.cardStatus ?? ''];
  const lifecycleKey = LIFECYCLE_STATUS_LABELS[publication?.lifecycleStatus ?? ''];
  const statusChipLabel = cardStatusKey
    ? t(cardStatusKey)
    : lifecycleKey
      ? t(lifecycleKey)
      : (publication?.lifecycleStatus ?? t('productPublicationCard.defaultStatusChip'));

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {t('productPublicationCard.title')}
          </Typography>
          <Button
            size="small"
            component={RouterLink}
            to="/catalog/publication"
            endIcon={<OpenInNewRoundedIcon fontSize="small" />}
          >
            {t('productPublicationCard.openWorkspaceLink')}
          </Button>
        </Stack>
        <Divider sx={{ mb: 2 }} />

        {publicationQuery.isLoading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              {t('productPublicationCard.loadingStatus')}
            </Typography>
          </Stack>
        ) : publicationQuery.isError ? (
          <Alert severity="error">{t('productPublicationCard.loadError')}</Alert>
        ) : (
          <Stack spacing={2}>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
              <Chip
                size="small"
                variant="outlined"
                color={productCardStatusColor(publication?.cardStatus ?? publication?.lifecycleStatus)}
                label={statusChipLabel}
              />
              {publication?.commercialAvailability ? (
                <Chip
                  size="small"
                  variant="outlined"
                  color={commercialAvailabilityColor(publication.commercialAvailability)}
                  label={
                    COMMERCIAL_AVAILABILITY_LABELS[publication.commercialAvailability]
                      ? t(COMMERCIAL_AVAILABILITY_LABELS[publication.commercialAvailability])
                      : publication.commercialAvailability
                  }
                />
              ) : null}
              <Chip
                size="small"
                color={publishedChannels.length > 0 ? 'success' : 'default'}
                variant={publishedChannels.length > 0 ? 'filled' : 'outlined'}
                label={
                  publishedChannels.length > 0
                    ? t('productPublicationCard.publishedChannelsChip', { count: publishedChannels.length })
                    : t('productPublicationCard.draftChip')
                }
              />
              {awaitingChannels.length > 0 ? (
                <Chip
                  size="small"
                  color="info"
                  variant="outlined"
                  label={t('productPublicationCard.includedChannelsChip', {
                    count: awaitingChannels.length,
                  })}
                />
              ) : null}
              {blockedChannels.length > 0 ? (
                <Chip
                  size="small"
                  color="error"
                  variant="outlined"
                  label={t('productPublicationCard.blockedChannelsChip', { count: blockedChannels.length })}
                />
              ) : null}
            </Stack>

            {channels.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('productPublicationCard.notPublishedAnywhere')}
              </Typography>
            ) : (
              <Stack spacing={1}>
                {channels.map((c) => (
                  <Stack
                    key={c.channelCode}
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    justifyContent="space-between"
                  >
                    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {channelLabel(c)}
                      </Typography>
                      {c.primaryCatalogId ? (
                        <Typography variant="caption" color="text.secondary" component="code">
                          {t('productPublicationCard.primaryCatalogLabel', { catalogId: c.primaryCatalogId })}
                        </Typography>
                      ) : null}
                    </Stack>
                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                      <Tooltip title={channelTooltip(t, c)}>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={publicationStatusColor(c.publicationStatus)}
                          label={
                            PUBLICATION_STATUS_LABELS[c.publicationStatus]
                              ? t(PUBLICATION_STATUS_LABELS[c.publicationStatus])
                              : c.publicationStatus
                          }
                        />
                      </Tooltip>
                      {c.missingRequiredFields?.slice(0, 3).map((field) => (
                        <Chip
                          key={`${c.channelCode}-${field}`}
                          size="small"
                          variant="outlined"
                          color="warning"
                          label={t('productPublicationCard.missingFieldChip', { field })}
                        />
                      ))}
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            )}

            <Alert severity="info" icon={<RuleRoundedIcon fontSize="inherit" />}>
              {t('productPublicationCard.ruleManagedHint')}
            </Alert>

            {canManage ? (
              <>
                <Divider />
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {isArchived ? (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={
                        restore.isPending ? <CircularProgress size={16} color="inherit" /> : <RestoreRoundedIcon />
                      }
                      disabled={pending}
                      onClick={runRestore}
                    >
                      {t('productPublicationCard.restore')}
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={
                        archive.isPending ? <CircularProgress size={16} color="inherit" /> : <ArchiveRoundedIcon />
                      }
                      disabled={pending}
                      onClick={() => setConfirmArchive(true)}
                    >
                      {t('productPublicationCard.archive')}
                    </Button>
                  )}
                </Stack>
              </>
            ) : (
              <Alert severity="info">{t('productPublicationCard.noPermission')}</Alert>
            )}
          </Stack>
        )}
      </CardContent>

      <Dialog open={confirmArchive} onClose={() => setConfirmArchive(false)}>
        <DialogTitle>{t('productPublicationCard.archiveDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('productPublicationCard.archiveDialog.body')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmArchive(false)}>{t('common.cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            startIcon={archive.isPending ? <CircularProgress size={16} color="inherit" /> : <ArchiveRoundedIcon />}
            disabled={archive.isPending}
            onClick={runArchive}
          >
            {t('productPublicationCard.archive')}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
